import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import { useEffect, useRef } from "react";

import { LocationGenerics } from "router/location";
import { UpdateTargetForm } from "components/forms";
import { UpdateTargetFormValues } from "components/forms/update-target";
import toast from "react-hot-toast";
import { updateTarget } from "../../_graphql/visal/mutations/target";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useSearch } from "react-location";
import { useTarget } from "hooks/data/use-targets";
import { wrapClick } from "utils";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Target name is required"),
  targetId: Yup.string().required(),
  start_date: Yup.string().required("Start date is required"),
  end_date: Yup.string()
    .required("End date is required")
    .test(
      "end-date-after-start-date",
      "End date must be after start date",
      function (value) {
        const { start_date } = this.parent;
        return !start_date || !value || new Date(value) >= new Date(start_date);
      }
    ),
  base_currency: Yup.string().required("Base currency is required"),
});

export default function UpdateTargetContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const { loading, targetBadge } = useTarget({
    targetId: searchParams?.id || "",
  });
  const initialSetupDone = useRef(false);

  const [execute, { loading: updating }] = useMutation(updateTarget, {
    refetchQueries: ["fetchTargets"],
  });

  const form = useFormik<UpdateTargetFormValues>({
    initialValues: {
      name: "",
      targetId: searchParams?.id || "",
      start_date: "",
      end_date: "",
      base_currency: "GHS",
    },
    validationSchema,
    onSubmit: async (variables, helpers) => {
      execute({
        variables: {
          id: variables.targetId,
          target_name: variables.name,
          start_date: variables.start_date,
          end_date: variables.end_date,
          base_currency: variables.base_currency,
        },
      })
        .then(({ data }) => {
          if (data?.updateTarget) {
            toast.success("Target updated successfully");
            setOpen(false);
            helpers.resetForm();
            refetch?.();
          } else {
            toast.error("Failed to update Target");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (targetBadge && !initialSetupDone.current) {
      form.setFieldValue("name", targetBadge.target_name || "");
      form.setFieldValue("start_date", targetBadge.start_date || "");
      form.setFieldValue("end_date", targetBadge.end_date || "");
      form.setFieldValue("base_currency", targetBadge.base_currency || "GHS");
      initialSetupDone.current = true;
    }
  }, [form, targetBadge]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title="Update Target"
      description="Update the details of Target below"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          loading={updating}
          disabled={updating}
          onClick={wrapClick(form.handleSubmit)}
        >
          {updating ? "Please wait..." : "Update Target"}
        </OfficeButton>
      )}
    >
      <UpdateTargetForm form={form} />
    </Modal>
  );
}
